import type { ExperienceType } from '@/types'

export const cvMetadata = {
  lastUpdated: '2026-11-03',
} as const

export const workExperiences: ExperienceType[] = [
  {
    id: 'daa-2026',
    title: 'Principal Platform Engineer (contract)',
    role: ['Principal', 'Cloud', 'Platform Engineering', 'Developer Experience'],
    company: 'Bitheater Ltd',
    finalClient: 'DAA',
    project: 'Platform architecture and developer enablement',
    startDate: new Date('2026-10-05'),
    achievements: [],
    tasks: [
      'Define technical direction and reusable engineering patterns across application teams.',
      'Design cloud infrastructure and shared software foundations for frontend, backend and data workloads.',
      'Build developer tooling and automation that simplify development and promote consistent delivery.',
      'Collaborate with engineering leadership, cloud and cybersecurity teams to align architectural approaches with organisational standards.',
      'Integrate security, reliability and maintainability into application design and delivery practices.',
      'Evaluate architectural approaches through prototypes, technical documentation and stakeholder review.',
    ],
    skills: [
      {label: 'Azure', value: 100},
      {label: 'GitHub Actions', value: 100},
      {label: 'Docker', value: 100},
      {label: 'Python', value: 100},
      {label: 'FastAPI', value: 100},
      {label: 'React', value: 100},
      {label: 'TypeScript', value: 100},
      {label: 'Developer Experience', value: 100},
    ],
    industry: 'aviation',
  },
  {
    id: 'british-airways-2025',
    title: 'Principal Platform Engineer (contract title: DevOps Engineer)',
    role: ['Principal', 'Platform Engineering', 'DevOps', 'Cloud', 'AI'],
    company: 'Parser',
    project: '<a class="text-inherit" href="https://www.britishairways.com">British Airways</a>',
    startDate: new Date('2025-06-09'),
    endDate: new Date('2026-09-30'),
    achievements: [],
    tasks: [
      "Owned the platform architecture and roadmap supporting 6 applications across 25 repositories, 24 services, 60 Dagster pipelines, and 24 environments across 24 AWS accounts, used by 36 engineers.",
      "Led the evolution of independently deployed React frontends, FastAPI services, and Dagster data pipelines on AWS ECS Fargate into a reusable internal platform.",
      "Designed and implemented the migration of the portfolio's only monorepo—containing React frontends, FastAPI services, and Dagster data pipelines—to the multi-repository model used across dozens of related projects.",
      "Documented the migration's trade-offs through ADRs and validated the platform design by migrating real projects as a proof of concept for stakeholders.",
      "Enabled services to evolve and deploy independently with service-specific pipelines and dependencies, while introducing explicit version and compatibility management between services.",
      "Designed local-development tooling that orchestrates multi-service applications with closer cloud-environment parity, mitigating the repository split's impact on integrated development and debugging.",
      "Defined Terraform standards and reusable Terraform modules, GitHub Actions workflows, and shared Python, React, and FastAPI modules across the application portfolio.",
      "Owned CI/CD, release processes, observability, cloud cost controls, and security tooling and policies, including AWS Inspector, Snyk, SonarQube, CodeScene, and Datadog.",
      "Provided technical mentorship across teams and participated in engineering selection through technical assessments.",
      "Influenced technology selection and architectural decisions while partnering with application and data-engineering teams."
    ],
    skills: [
      {label: 'Terraform', value: 100},
      {label: 'AWS', value: 100},
      {label: 'GitHub Actions', value: 100},
      {label: 'CodeScene', value: 100},
      {label: 'SonarQube', value: 100},
      {label: 'Snyk', value: 100},
      {label: 'AWS Inspector', value: 100},
      {label: 'Docker', value: 100},
      {label: 'SageMaker', value: 100},
      {label: 'ECS Fargate', value: 100},
      {label: 'FastAPI', value: 100},
      {label: 'React', value: 100},
      {label: 'Dagster', value: 100},
      {label: 'Platform Engineering', value: 100},
      {label: 'DevSecOps', value: 100},
      {label: 'Developer Experience', value: 100},
      {label: 'Observability', value: 100},
      {label: 'Cloud Cost Management', value: 100},
    ],
    industry: 'aviation'
  },
  {
    id: 'checkout-com-2024',
    title: 'SecOps Consultant (contractor)',
    role: ['Lead', 'DevOps', 'SecOps'],
    company: 'Parser',
    project: '<a class="text-inherit" href="https://checkout.com">Checkout.com</a>',
    startDate: new Date('2024-09-13'),
    endDate: new Date('2025-06-09'),
    achievements: [],
    tasks: [
      "Enforced and enhanced least-privilege principles across cloud infrastructure and internal systems.",
      "Supported third-party security audits by providing technical evidence and ensuring compliance with security best practices.",
      "Developed monitoring and alerting mechanisms to detect and respond to privilege-policy violations in real time.",
      "Improved Infrastructure as Code workflows and secure storage practices for sensitive data using Terraform and Spacelift."
    ],
    skills: [
      {label: 'Terraform', value: 100},
      {label: 'Spacelift', value: 100},
      {label: 'AWS', value: 100},
      {label: 'Retool', value: 100},
      {label: 'Git', value: 100},
      {label: 'IT Strategy', value: 100},
      {label: 'Team Lead', value: 100},
      {label: 'Docker', value: 100},
    ],
    industry: 'finance'
  },
  {
    id: 'blink-galaxy-2024',
    title: 'DevOps Tech Lead',
    role: ['Lead', 'DevOps'],
    company: 'Nexxyo Labs',
    project: '<a class="text-inherit" href="https://blinkgalaxy.com">Blink Galaxy</a>',
    startDate: new Date('2024-03-13'),
    endDate: new Date('2024-07-13'),
    achievements: [
      "Redesigned infrastructure using IaC with Jenkins, Kubernetes, Terraform, Packer, and Azure."
    ],
    tasks: [
      "Analyzed and documented the existing infrastructure, identifying opportunities to improve cost and performance.",
      "Built and maintained CI/CD pipelines to automate infrastructure provisioning and streamline deployments.",
      "Onboarded a DevOps engineer and provided guidance on internal tools, workflows, and cloud infrastructure.",
      "Provided cross-team architectural guidance on Azure and cloud best practices."
    ],
    skills: [
      {label: 'Kubernetes', value: 100},
      {label: 'Terraform', value: 100},
      {label: 'Jenkins', value: 100},
      {label: 'Azure', value: 100},
      {label: 'OVH', value: 100},
      {label: 'Git', value: 100},
      {label: 'IT Strategy', value: 100},
      {label: 'Team Lead', value: 100},
      {label: 'Unity', value: 100},
      {label: 'Unreal', value: 100},
    ],
    industry: 'gaming'
  },
  {
    id: 'bouncybrain-2023',
    title: 'Lead Software Engineer',
    role: ['Lead', 'Developer'],
    company: 'BouncyBrain',
    project: '<a class="text-inherit" href="https://www.bouncybraingames.com/projects/">FullSteam: Fluffy X Machina</a>',
    startDate: new Date('2023-12-13'),
    endDate: new Date('2024-03-13'),
    achievements: [
      "Led technical development of BouncyBrain game MVP, securing initial investment."
    ],
    tasks: [
      "Defined technical strategy and coding workflows for BouncyBrain.",
      "Designed and programmed game MVP using Unity and C#.",
      "Managed code reviews and ensured high-quality standards.",
      "Provided strategic leadership in business development and IT strategy."
    ],
    skills: [
      {label: 'Unity', value: 90},
      {label: 'C#', value: 100},
      {label: 'Git', value: 100},
      {label: 'Business Development', value: 1},
      {label: 'IT Strategy', value: 100},
      {label: 'Team Lead', value: 100},
      {label: 'Blender', value: 1},
      {label: 'Kubernetes', value: 90},
      {label: 'Jenkins', value: 90},
    ],
    industry: 'gaming',
    breakPageAfter: false
  },
  {
    id: 'digit-2022',
    title: 'DevOps Engineer',
    role: ['DevOps', 'Cloud'],
    company: 'DIGIT, a Scopely Studio',
    project: '<a class="text-inherit" href="https://startrekfleetcommand.com/">Star Trek Fleet Command</a>',
    startDate: new Date('2022-07-01'),
    endDate: new Date('2023-12-12'),
    achievements: [
      "Achieved a 30% infrastructure cost reduction and a 20% Redis Labs cost reduction.",
      "Enhanced Dev environments with Twingate & Okta SSO integration.",
      "Successfully migrated Jenkins from 1.0 to 2.0."
    ],
    tasks: [
      "Designed and implemented AWS infrastructure using Terraform (ECS, Lambda, DynamoDB).",
      "Optimized infrastructure for performance, scalability, and cost efficiency using Datadog, CloudWatch, and Kibana metrics.",
      "Provided AWS expertise and best practices guidance to teams.",
      "Managed Docker image creation, CI/CD pipelines (Jenkins, GitLab CI), and developed Python scripts for AWS Lambda.",
      "Established monitoring with Datadog and Kibana for earlier issue detection."
    ],
    skills: [
      {label: 'AWS', value: 100},
      {label: 'Terraform', value: 100},
      {label: 'Jenkins', value: 100},
      {label: 'Python', value: 100},
      {label: 'Datadog', value: 100},
      {label: 'Docker', value: 100},
      {label: 'Linux', value: 100},
      {label: 'GitLab CI', value: 100},
      {label: 'Unity', value: 100},
      {label: 'Git', value: 100},
      {label: 'Redis', value: 100},
    ],
    industry: 'gaming',
    breakPageAfter: true
  },
  {
    id: 'dovetailgames-2021',
    title: 'Full Stack Developer / DevOps',
    role: ['Senior', 'Developer'],
    company: 'Bitheater Ltd (Contracts)',
    finalClient: 'Dovetailgames',
    project: '<a class="text-inherit" href="https://www.trainsimworld.com/?_ga=2.258825162.1532421791.17010054840-69028514.17010054840">Train Sim World 4</a>',
    startDate: new Date('2021-05-01'),
    endDate: new Date('2022-07-01'),
    achievements: [],
    tasks: [
      "Improved and extended an established monorepo for a real-time gaming platform using Nest.js, Next.js, and TypeScript.",
      "Designed and implemented an end-to-end testing process with Cucumber, integrating automated cross-service validation into GitLab CI.",
      "Designed REST APIs for the game and Creators Club website and delivered application features and refactoring within the existing architecture.",
      "Improved CI/CD workflows with GitLab CI and Docker.",
      "Optimized Linux and Nginx infrastructure for improved performance and cost-efficiency.",
      "Managed Google Cloud Services (GCP) focusing on App Engine, Docker, and Kubernetes."
    ],
    skills: [
      {label: 'Kubernetes', value: 100},
      {label: 'TDD', value: 100},
      {label: 'GCP', value: 100},
      {label: 'PostgreSQL', value: 100},
      {label: 'Docker', value: 90},
      {label: 'GitLab CI', value: 100},
      {label: 'Nest.js', value: 100},
      {label: 'TypeScript', value: 100},
      {label: 'Next.js', value: 100},
      {label: 'Node.js', value: 100},
      {label: 'Git', value: 100},
    ],
    industry: 'gaming',
  },
  {
    id: 'blue-ocean-2020',
    title: 'Lead DevOps / Full Stack Developer',
    role: ['Lead', 'Developer'],
    company: 'Bitheater Ltd (Contracts)',
    finalClient: 'Blue Ocean Sustainability',
    project: '<a class="text-inherit" href="https://truecarbonscore.com">Truecarbon Score</a>',
    startDate: new Date('2020-08-01'),
    endDate: new Date('2021-05-01'),
    achievements: [],
    tasks: [
      "Created and implemented API for application migration and M2M connections.",
      "Led design and development of Truecarbon Score app with Laravel, Vue.js, and Inertia.",
      "Provided project owner reporting for transparent project communication.",
      "Managed a small development team, onboarding four developers and coordinating delivery against project milestones.",
      "Managed AWS services, focusing on Beanstalk for deployment and scalability."
    ],
    skills: [
      {label: 'Bitbucket', value: 20},
      {label: 'Laravel', value: 100},
      {label: 'Vue.js', value: 100},
      {label: 'TypeScript', value: 100},
      {label: 'Bash', value: 100},
      {label: 'Inertia', value: 100},
      {label: 'Vite', value: 100},
      {label: 'MySQL', value: 40},
      {label: 'Docker', value: 100},
      {label: 'AWS', value: 100},
      {label: 'Terraform', value: 30},
      {label: 'Team Lead', value: 100},
      {label: 'Bash', value: 10},
      {label: 'Tailwind', value: 100},
      {label: 'Git', value: 100},
    ],
    industry: 'building',
    breakPageAfter: false
  },
  {
    id: 'alfatec-2020',
    title: 'Full Stack Developer',
    role: ['Developer'],
    company: 'Bitheater Ltd (Contracts)',
    finalClient: 'Alfatec',
    project: '<a class="text-inherit" href="https://www.sinasuite.com.com">SINA HIS (Hospital Information System)</a>',
    startDate: new Date('2020-02-01'),
    endDate: new Date('2020-08-01'),
    achievements: [],
    tasks: [
      "Developed REST API and applications using JAVA, Spring, and Angular for high-quality software delivery.",
      "Refactored MySQL codebase to enhance maintainability and readability.",
      "Implemented key features with JBoss (Firefly) to expand the company's customer base."
    ],
    skills: [
      {label: 'Spring', value: 100},
      {label: 'Java', value: 100},
      {label: 'Angular', value: 20},
      {label: 'MySQL', value: 100},
      {label: 'JBoss', value: 100},
      {label: 'TDD', value: 100},
      {label: 'Git', value: 100},
      {label: 'TypeScript', value: 100},
    ],
    industry: 'health',
  },
  {
    id: 'indra-2018',
    title: 'Lead C++ Developer',
    role: ['Lead', 'Developer'],
    finalClient: 'Indra',
    company: 'Bitheater Ltd (Contracts)',
    startDate: new Date('2018-01-01'),
    endDate: new Date('2020-02-01'),
    achievements: [],
    tasks: [
      "Led migration from Microsoft libs to cross-platform (Linux + Windows) for Intelligent Traffic System (ITS).",
      "Refactored and optimized C/C++ codebase for better maintainability and performance.",
      "Utilized C++, Qt, and CMake for successful migration across Linux and Windows.",
      "Implemented Boost libraries to enhance ITS functionality.",
      "Collaborated on platform-specific challenges for seamless integration.",
      "Achieved Linux compatibility for tenders requiring Linux support.",
      "Managed press articles:\n  - Article 1: https://bit.ly/2Z11mgC\n  - Article 2: https://bit.ly/30AVGva"
    ],
    skills: [
      {label: 'C/C++', value: 100},
      {label: 'Qt', value: 100},
      {label: 'CMake', value: 100},
      {label: 'Bash', value: 100},
      {label: 'VirtualBox', value: 100},
      {label: 'Linux', value: 100},
      {label: 'Boost', value: 100},
      {label: 'Team Management', value: 100},
      {label: 'Team Lead', value: 100},
      {label: 'Git', value: 100},
      {label: '.NET', value: 100},
    ],
    industry: 'public infrastructure',
  },
  {
    id: 'iot-2017',
    title: 'Lead Python Developer',
    role: ['Lead', 'Developer'],
    company: 'Bitheater Ltd (Contracts)',
    startDate: new Date('2017-06-01'),
    endDate: new Date('2018-01-01'),
    achievements: [],
    tasks: [
      "Led IoT device prototyping using Python on Raspberry Pi and C on nRF51 SoC.",
      "Performed nRF51 firmware maintenance and upgrades.",
      "Implemented IoT business logic on Raspberry Pi using Python for rapid testing and feature additions.",
      "Conducted extensive testing of NFC, 6LowPan, GSM, and TCP/IP for internet access, informing patent requests.",
      "Filed patent requests to protect developed mechanisms for data exchange (see Certifications section for links)."
    ],
    skills: [
      {label: 'firmware', value: 100},
      {label: 'SoC', value: 100},
      {label: 'C/C++', value: 100},
      {label: 'Python', value: 100},
      {label: 'Flask', value: 100},
      {label: 'NFC', value: 100},
      {label: 'GSM', value: 100},
      {label: '6LowPan', value: 100},
      {label: 'IoT', value: 100},
      {label: 'Node.js', value: 100},
      {label: 'Express.js', value: 100},
      {label: 'BLE', value: 100},
      {label: 'Team Lead', value: 100},
      {label: 'Git', value: 100},
      {label: 'iOS', value: 100},
      {label: 'PWA', value: 100},
      {label: 'Webbluetooth', value: 100},
    ],
    industry: 'ecommerce',
  },
  {
    id: 'ecommerce-2015',
    title: 'Lead DevOps',
    role: ['Lead', 'DevOps'],
    company: 'Bitheater Ltd (Contracts)',
    startDate: new Date('2014-08-01'),
    endDate: new Date('2017-06-01'),
    achievements: [],
    tasks: [
      "Automated and maintained E-Commerce platform infrastructure for optimal performance and reliability.",
      "Developed CI/CD processes for Progressive Web App development, enabling rapid releases to staging and production.",
      "Implemented backup and disaster recovery processes to meet ISO standards certifications.",
      "Managed and configured Atlassian platform tools: GIT/Bitbucket, Bamboo, and Jira.",
      "Led the migration from on-premises infrastructure to a scalable cloud architecture.",
      "Improved platform stability through network troubleshooting, monitoring, and proactive infrastructure maintenance."
    ],
    skills: [
      {label: 'Ansible', value: 100},
      {label: 'Docker', value: 100},
      {label: 'Bitbucket', value: 40},
      {label: 'Bamboo', value: 100},
      {label: 'PHPUnit', value: 40},
      {label: 'PHING', value: 40},
      {label: 'Azure', value: 100},
      {label: 'Linux', value: 100},
      {label: 'Nagios', value: 100},
      {label: 'Newrelic', value: 100},
      {label: 'Team Lead', value: 100},
      {label: 'Bash', value: 100},
      {label: 'Wordpress', value: 100},
      {label: 'Git', value: 100},
      {label: 'Apache', value: 100},
      {label: 'MySQL Galera', value: 100},
      {label: 'Magento', value: 100},
      {label: 'Pen Testing', value: 100},
    ],
    industry: 'ecommerce',
    breakPageAfter: false
  },
  {
    id: 'lendico-2013',
    role: ['Developer'],
    achievements: [],
    title: 'Full Stack Developer',
    company: 'Bitheater Ltd (Contracts)',
    finalClient: 'Rocket Internet GMbH',
    project: '<a class="text-inherit" href="https://es.wikipedia.org/wiki/Lendico">Lendico</a>',
    startDate: new Date('2013-12-01'),
    endDate: new Date('2014-08-01'),
    tasks: [
      "Developed P2B lending platform emphasizing functionality, security, and scalability.",
      "Refactored application to maintain compatibility with P2P lending platform, using a unified codebase."
    ],
    skills: [
      {label: 'AWS', value: 10},
      {label: 'Linux', value: 100},
      {label: 'Symfony', value: 100},
      {label: 'MySQL', value: 1},
      {label: 'Bootstrap', value: 100},
      {label: 'JQuery', value: 100},
      {label: 'Git', value: 100},
    ],
    industry: 'finance',
  },
  {
    id: 'movistar-android-2013',
    role: ['Lead', 'Developer'],
    achievements: [],
    title: 'Lead Android Developer',
    company: 'Telefonica',
    project: 'Por Ser Movistar',
    startDate: new Date('2013-05-01'),
    endDate: new Date('2013-12-01'),
    tasks: [
      "Led development of native Android app with SSO (SAML) for 20M Movistar clients.",
      "Published Android app for seamless access to exclusive offers.",
      "Developed deployment tools and statistics compilation for streamlined processes.",
      "Configured Apache servers for performance and security.",
      "Implemented high-availability systems for continuous service availability."
    ],
    skills: [
      {label: 'Android', value: 100},
      {label: 'Java', value: 100},
      {label: 'SAML', value: 100},
      {label: 'Team Lead', value: 100},
      {label: 'SourceSafe', value: 100},
    ],
    industry: 'telecommunications'
  },
  {
    id: 'movistar-web-2012',
    role: ['Lead', 'Developer'],
    achievements: [],
    title: 'Lead Full Stack Developer',
    company: 'Telefonica',
    project: 'Por Ser Movistar',
    startDate: new Date('2012-12-01'),
    endDate: new Date('2013-05-01'),
    tasks: [
      "Led development of promotional site and REST API for exclusive offers and coupons for Movistar clients.",
      "Created web platform for Movistar customers to access exclusive offers and coupons.",
      "Developed REST service as foundation for future mobile app development.",
      "Led Android app development for seamless user access to offers.",
      "Contributed to web development using PHP/Symfony, REST services, and SSO (SAML) integration.",
      "Developed SSO SDK for integration with Movistar's SAML identity provider.",
      "Implemented high-availability systems for continuous service availability."
    ],
    skills: [
      {label: 'SDK Development', value: 100},
      {label: 'Symfony', value: 100},
      {label: 'JQuery', value: 100},
      {label: 'JavaScript', value: 100},
      {label: 'Linux', value: 100},
      {label: 'Apache', value: 100},
      {label: 'MySQL', value: 90},
      {label: 'SAML', value: 100},
      {label: 'Nginx', value: 100},
      {label: 'AWS', value: 1},
      {label: 'Team Lead', value: 100},
      {label: 'Git', value: 100},
    ],
    industry: 'telecommunications',
    breakPageAfter: false
  },
  {
    id: 'uji-data-mining-2012',
    role: ['Developer'],
    achievements: [],
    title: 'Software Engineer',
    company: 'Universitat Jaume I',
    startDate: new Date('2012-06-01'),
    endDate: new Date('2012-12-01'),
    tasks: [
      "Developed data-mining and web-crawling software using Java and Python.",
      "Worked with OWL ontologies to structure and analyze research data."
    ],
    skills: [
      {label: 'Java', value: 100},
      {label: 'Python', value: 100},
      {label: 'Data Mining', value: 100},
      {label: 'Web Crawling', value: 100},
      {label: 'OWL', value: 100},
    ],
    industry: 'learning',
  },
  {
    id: 'uji-drupal-2011',
    role: ['Developer'],
    achievements: [],
    title: 'Full Stack Developer',
    company: 'Universitat Jaume I',
    startDate: new Date('2011-06-01'),
    endDate: new Date('2012-06-01'),
    tasks: [
      'Developed a web application for the management of the university\'s research projects using Drupal',
    ],
    skills: [
      {label: 'Drupal', value: 100},
      {label: 'Git', value: 100},
    ],
    industry: 'learning',
  },
  {
    id: 'uji-robotics-2010',
    role: ['Senior', 'Developer'],
    achievements: [],
    title: 'Software Engineer',
    company: 'Universitat Jaume I',
    startDate: new Date('2010-12-01'),
    endDate: new Date('2011-06-01'),
    tasks: [
      "Developed applications for humanoid robotics, contributing to field advancements.",
      "Implemented neural network training systems for humanoid robotics.",
      "Developed computer vision applications with OpenCV.",
      "Contributed to projects documented at IEEE and Robinlab.",
      "Worked on the humanoid torso robot project at Robinlab."
    ],
    skills: [
      {label: 'C/C++', value: 100},
      {label: 'OpenCV', value: 100},
      {label: 'QT', value: 100},
      {label: 'ROS', value: 100},
      {label: 'CMake', value: 100},
      {label: 'Git', value: 100},
      {label: 'ML', value: 100},
      {label: 'Neural Networks', value: 100},
    ],
    industry: 'robotics',
  },
  {
    id: 'inovacion-dental-2010',
    role: ['Senior', 'Developer'],
    achievements: [],
    title: 'Software Engineer',
    company: 'INNOVACIÓN DENTAL',
    startDate: new Date('2010-05-01'),
    endDate: new Date('2010-12-01'),
    tasks: [
      "Managed and visualized DICOM images with VTK/ITK libraries for medical image processing.",
      "Developed Dental DICOM Images Analysis application for specialized medical imaging.",
      "Created Mitsubishi Robotic Arm application for dental procedure automation."
    ],
    skills: [
      {label: 'VTK/ITK', value: 100},
      {label: 'DICOM', value: 100},
      {label: 'Swift', value: 100},
      {label: 'OpenCV', value: 100},
      {label: 'Java', value: 100},
      {label: 'Git', value: 100},
      {label: 'C/C++', value: 100},
    ],
    industry: 'health',
  },
  {
    id: 'overon-2009',
    role: ['Developer'],
    achievements: [],
    title: 'Software/DevOps Engineer',
    company: 'OVERON SL',
    startDate: new Date('2009-06-01'),
    endDate: new Date('2010-02-01'),
    tasks: [
      "Designed and maintained internal applications for enterprise management.",
      "Managed databases and email servers for seamless operation of internal applications using Linux, Apache, and MySQL.",
      "Developed Client/Server Applications to meet business requirements, utilizing PHP."
    ],
    skills: [
      {label: 'Linux', value: 100},
      {label: 'Apache', value: 100},
      {label: 'MySQL', value: 100},
      {label: 'PHP', value: 100},
      {label: 'Bash', value: 100},
      {label: 'CSS', value: 100},
    ],
    industry: 'telecommunications',
    breakPageAfter: true
  },
  {
    id: 'thyssen-2009',
    role: ['Developer'],
    achievements: [],
    title: 'Software/DevOps Engineer',
    company: 'Tecknosfera',
    finalClient: 'Thyssen-Bornemisza',
    startDate: new Date('2009-03-01'),
    endDate: new Date('2009-06-01'),
    tasks: [
      "Developed PHP SDK for building CMS managed web applications.",
      "Contributed to services at an interactive communication agency.",
      "Designed and developed websites for seamless user experience.",
      "Created multimedia content for engaging online experiences.",
      "Provided internet consulting services for strategic online presence.",
      "Contributed to branding, positioning, and corporate identity online.",
      "Resolved issues in enterprise web portals to maintain functionality.",
      "Developed enterprise web portals using PHP and CodeIgniter, demonstrating expertise in Linux, Apache, MySQL, and PHP."
    ],
    skills: [
      {label: 'SDK development', value: 100},
      {label: 'Linux', value: 90},
      {label: 'Apache', value: 90},
      {label: 'MySQL', value: 90},
      {label: 'PHP', value: 100},
      {label: 'CodeIgniter', value: 100},
      {label: 'Typo3', value: 100},
      {label: 'CSS', value: 90},
      {label: 'JavaScript', value: 90},
      {label: 'Jquery', value: 90},
      {label: 'Git', value: 100},
    ],
    industry: 'learning',
  },
  {
    id: 'hughes-2008',
    role: ['Developer'],
    achievements: [],
    title: 'Full Stack Developer',
    company: 'Hughes Media Internet Ltd',
    startDate: new Date('2008-03-01'),
    endDate: new Date('2009-02-01'),
    tasks: [
      "Improved and maintained company's CMS SDK.",
      "Conducted PHP web development for dynamic and interactive websites.",
      "Developed CMS solutions using MySQL, Linux, and PHP5, focusing on eCommerce and MVC architecture.",
      "Utilized Object-Oriented Programming (OOP) principles for efficient code development.",
      "Monitored and troubleshooted web and mail servers for performance and reliability.",
      "Contributed to development, maintenance, and integration of various websites.",
      "Developed and deployed SOAP web services to enhance functionality.",
      "Administered CentOS Linux servers for seamless operation."
    ],
    skills: [
      {label: 'SDK development', value: 100},
      {label: 'Linux', value: 70},
      {label: 'Apache', value: 70},
      {label: 'MySQL', value: 100},
      {label: 'PHP', value: 100},
      {label: 'Pen Testing', value: 20},
      {label: 'Jquery', value: 90},
      {label: 'JavaScript', value: 100},
      {label: 'Subversion', value: 100},
    ],
    industry: 'ecommerce',
  },
  {
    id: 'eads-2004',
    role: ['Developer'],
    achievements: [],
    title: 'Full Stack Developer',
    company: 'EADS CASA Espacio',
    startDate: new Date('2004-06-01'),
    endDate: new Date('2007-11-01'),
    tasks: [
      "Developed warehouse and providers management application with PHP and MySQL.",
      "Created non-conformance reports (NCR) management application using PHP, HTML, CSS, and MySQL.",
      "Contributed to development of enterprise internal network services, including intranet and partner data transfer.",
      "Resolved technical issues, conducted troubleshooting, and developed tools for efficiency.",
      "Designed graphic user interfaces for data consultation, modification, and insertion.",
      "Developed data exchange interface between Oracle and MySQL.",
      "Created autoclave management application with Visual Basic 5.0.",
      "Facilitated information exchange between partners and company, including SAP interfaces."
    ],
    skills: [
      {label: 'Java', value: 10},
      {label: 'Linux', value: 90},
      {label: 'PHP', value: 100},
      {label: 'ORACLE', value: 20},
      {label: 'CSS', value: 90},
      {label: 'JavaScript', value: 90},
      {label: 'Jquery', value: 90},
      {label: 'ANSI C', value: 20},
      {label: 'AWK', value: 30},
      {label: 'Visual Basic 5.0', value: 40},
      {label: 'MySQL', value: 100},
      {label: 'Apache', value: 90},
      {label: 'SAP', value: 1},
      {label: 'Subversion', value: 100},
    ],
    industry: 'aeronautics',
  },
  {
    id: 'eads-admin-2001',
    role: ['Admin'],
    achievements: [],
    title: 'Systems Administrator',
    company: 'EADS CASA Espacio',
    startDate: new Date('2001-06-01'),
    endDate: new Date('2004-06-01'),
    tasks: [
      "Administered AIX/UNIX/Linux servers, overseeing various services for optimal performance.",
      "Configured and tuned Apache2, including mod_proxy, mod_php, and virtual domains, for efficient web services.",
      "Performed troubleshooting, upgrades, and configuration of services/systems on AIX/UNIX/Linux/Solaris servers.",
      "Managed databases to ensure integrity and availability for critical operations.",
      "Implemented and maintained MySQL backup system for data integrity.",
      "Provided technical support for CATIA users."
    ],
    skills: [
      {label: 'AIX', value: 70},
      {label: 'Linux', value: 90},
      {label: 'MySQL', value: 80},
      {label: 'Apache', value: 70},
      {label: 'CATIA', value: 30},
      {label: 'C/C++', value: 30},
      {label: 'AWK', value: 30},
      {label: 'Bash', value: 30},
    ],
    industry: 'aeronautics',
    breakPageAfter: false
  },
  {
    id: 'freelance-teacher-1999',
    role: ['Teacher'],
    achievements: [],
    title: 'Freelance Teacher',
    company: 'Informática 64 SL.',
    startDate: new Date('1999-06-01'),
    endDate: new Date('2002-11-01'),
    tasks: [
      "Conducted training sessions for UNIX shell scripting.",
      "Delivered comprehensive Master classes on C/C++ and Linux sponsored by the Madrid Council.",
      "Taught application development course with Java, sponsored by the Madrid Council."
    ],
    skills: [
      {label: 'Linux', value: 100},
      {label: 'Bash', value: 100},
      {label: 'C/C++', value: 100},
      {label: 'Java', value: 100},
    ],
    industry: 'learning',
  }
]

export const personalInfo = {
  name: 'Jonathan Ruiz',
  title: 'Principal Platform Engineer | AWS, Azure & Developer Experience',
  email: 'contact@jonathanruiz.co.uk',
  location: 'Castellón, Spain · Open to relocate to London · UK sponsorship required',
  summary: 'Principal Platform Engineer with over 25 years across software, systems, and cloud engineering. Builds reusable delivery platforms across AWS and Azure, combining architecture, release reliability, security integration and developer experience. Previously led platform standards across a portfolio used by 36 engineers.',
  skills: {
    primary: ['Platform Engineering', 'AWS', 'Azure', 'Terraform', 'Cloud Architecture'],
    technologies: ['Kubernetes', 'ECS Fargate', 'Azure Container Apps', 'Docker', 'GitHub Actions', 'Dagster', 'FastAPI', 'Datadog'],
    languages: ['Python', 'JavaScript', 'C#', 'Bash', 'PowerShell']
  }
}

export const anthropicCv = {
  title: 'Principal Platform Engineer — Developer Productivity & CI/CD',
  featuredExperienceIds: [
    'daa-2026',
    'british-airways-2025',
    'checkout-com-2024',
    'blink-galaxy-2024',
    'digit-2022',
    'dovetailgames-2021',
  ],
  skillSections: [
    {
      title: 'Developer Productivity',
      skills: ['Platform Engineering', 'GitHub Actions', 'Jenkins', 'GitLab CI', 'CI/CD', 'Developer Experience', 'Release Engineering', 'Cucumber'],
    },
    {
      title: 'Infrastructure',
      skills: ['AWS', 'Terraform', 'Docker', 'Kubernetes', 'ECS Fargate', 'GCP', 'Observability', 'DevSecOps'],
    },
    {
      title: 'Software Engineering',
      skills: ['Python', 'TypeScript', 'FastAPI', 'React', 'Nest.js', 'Next.js', 'Dagster', 'REST APIs'],
    },
  ],
} as const

// Additional comprehensive skills list
export const additionalSkills = [
  'CSS', 'HTML', 'MaterialUI', 'Lens', 'Cucumber', 'Bitbucket', 'Inertia', 'Vite', 'MySQL', 'Tailwind', 
  'JBoss', 'Qt', 'CMake', 'VirtualBox', 'Boost', 'Team Management', 'Firmware', 'SoC', 'Flask', 'NFC', 'GSM',
  '6LowPan', 'IoT', 'Express.js', 'BLE', 'iOS', 'PWA', 'Webbluetooth', 'Bamboo', 'PHPUnit', 'PHING', 
  'Nagios', 'Newrelic', 'Siege', 'Wordpress', 'Apache', 'MySQL Galera', 'Magento', 'Pen Testing',
  'Puppet', 'Bootstrap', 'JQuery', 'Vagrant', 'Android', 'SAML', 'SourceSafe', 'SDK Development', 
  'Drupal', 'OpenCV', 'ROS', 'ML', 'Neural Networks', 'VTK/ITK', 'DICOM', 'Swift', 'CodeIgniter', 
  'Typo3', 'Subversion', 'ORACLE', 'ANSI C', 'AWK', 'Visual Basic 5.0', 'SAP', 'AIX', 'CATIA',
  'Kibana', 'Nginx', 'Ansible', 'Vue.js', 'Node.js'
]

export const certifications = [
  {
    name: 'Certified Kubernetes Administrator (CKA)',
    issuer: 'Cloud Native Computing Foundation',
    date: 'Earned August 2024; valid until 30 August 2026',
    id: 'LF-ynhdss52m7'
  }
]

export const education = [
  {
    degree: 'Computer Science degree',
    institution: 'Universitat Jaume I',
    year: '1999',
    location: 'Castellón, Spain'
  }
]
